import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { portfolioAPI } from 'api/PortfolioAPI';
import { signTypedData, SignTypedDataParameters } from '@wagmi/core';
import {
  getStoredTokens,
  removeToken,
  saveToken,
  isTokenExpired,
} from 'structure/portfolio/utils/portfolio';
import { wagmiConfig } from 'structure/providers/wagmiConfig';

export type OwnerInfo = {
  signature: string;
  msgParams: SignTypedDataParameters;
};

export type VerifyUserErrorType = 'network' | 'cors' | 'unknown';

const classifyVerifyError = (error: unknown): VerifyUserErrorType => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'network';
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code?: string }).code === 'string' &&
    (error as { code?: string }).code === 'ERR_NETWORK'
  ) {
    return 'cors';
  }

  if (error instanceof Error) {
    const message = error.message?.toLowerCase() ?? '';
    if (message.includes('cors')) {
      return 'cors';
    }
    if (message.includes('network')) {
      return 'network';
    }
  }

  return 'unknown';
};

const fetchOwnerInfo = async (
  nonce: string,
  address: string,
  chainId: number
): Promise<OwnerInfo | null> => {
  if (!address || !chainId) {
    console.error('Ethereum provider or necessary information is missing');
    return null;
  }

  const isValidEthereumAddress = (
    address: string
  ): address is `0x${string}` => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  if (!isValidEthereumAddress(address)) {
    console.error('Invalid Ethereum address');
    return null;
  }
  const msgParams: SignTypedDataParameters = {
    domain: {
      chainId: chainId,
      name: 'Launchpad Authentication',
      version: '1',
    },
    message: {
      content:
        'Please sign this message to verify the ownership of your wallet',
      nonce,
    },
    primaryType: 'Nonce',
    types: {
      EIP712Domain: [
        { name: 'name', type: 'string' },
        { name: 'version', type: 'string' },
        { name: 'chainId', type: 'uint256' },
      ],
      Nonce: [
        { name: 'content', type: 'string' },
        { name: 'nonce', type: 'string' },
      ],
    },
    account: address,
  };

  try {
    if (!address) {
      throw new Error('Address is not defined');
    }
    const signature = await signTypedData(wagmiConfig, msgParams);
    return { signature, msgParams };
  } catch (error) {
    console.error(`Error signing message: ${(error as Error).message}`);
    return null;
  }
};

export const useUserDetails = (verified: boolean, address?: string) => {
  return useQuery({
    queryKey: ['userDetails', address],
    queryFn: () => portfolioAPI.getUserDetails({ address: address ?? '' }),
    enabled: verified && !!address,
  });
};

export const useRefreshUser = (
  address?: string,
  isRefresh: boolean = false
) => {
  return useQuery({
    queryKey: ['useRefreshUser', address],
    queryFn: () => portfolioAPI.refreshUser({ address: address ?? '' }),
    enabled: isRefresh && !!address,
  });
};

export const useVerifyUser = (address: string, chainId: number) => {
  const [verifiedData, setVerifiedData] = useState<OwnerInfo | null>(null);
  const [verified, setVerified] = useState<boolean>(false);
  const [verifyLoading, setVerifyLoading] = useState<boolean>(false);
  const [verifyError, setVerifyError] = useState<VerifyUserErrorType | null>(
    null
  );

  const checkTokenPresence = useCallback(() => {
    if (!address) return false;
    const tokens = getStoredTokens();
    const token = tokens[address];

    if (token && !isTokenExpired(token)) {
      return true;
    }

    if (token && isTokenExpired(token)) {
      removeToken(address);
      setVerified(false);
      setVerifiedData(null);
      return false;
    }

    return false;
  }, [address]);

  useEffect(() => {
    setVerified(checkTokenPresence());
  }, [checkTokenPresence]);

  useEffect(() => {
    const tokens = getStoredTokens();
    if (!address || !tokens[address]) {
      if (address) {
        removeToken(address);
      }
      setVerified(false);
      setVerifiedData(null);
    } else {
      const token = tokens[address];
      if (isTokenExpired(token)) {
        removeToken(address);
        setVerified(false);
        setVerifiedData(null);
      }
    }
  }, [address]);

  const verifyUser = useCallback(async () => {
    if (!address || !chainId) return null;
    setVerifyError(null);
    setVerifyLoading(true);
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        setVerifyError('network');
        return null;
      }

      const nonceData = await portfolioAPI.fetchNonce({ address });
      if (!nonceData?.nonce) {
        throw new Error('Nonce not found');
      }

      const result = await fetchOwnerInfo(nonceData.nonce, address, chainId);
      if (!result || !result.signature) {
        throw new Error('Failed to fetch owner info');
      }

      const verify = await portfolioAPI.verifyOwnerInfo({
        address,
        signature: result?.signature,
        chainId,
        message: JSON.stringify(result.msgParams),
      });

      if (verify.success) {
        saveToken(address, verify?.jwt ?? '');
        setVerified(true);
        setVerifiedData(result);
        setVerifyError(null);
        return result;
      } else {
        setVerified(false);
        setVerifiedData(null);
        setVerifyError('unknown');
        return null;
      }
    } catch (error) {
      console.error(
        `Error fetching or verifying owner info: ${(error as Error).message}`
      );
      setVerified(false);
      setVerifiedData(null);
      setVerifyError(classifyVerifyError(error));
      return null;
    } finally {
      setVerifyLoading(false);
    }
  }, [address, chainId]);

  return {
    verifyUser,
    verifiedData,
    verified,
    verifyLoading,
    setVerified,
    verifyError,
  };
};
